import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_590a4cbe-6d2f-42fa-9080-72b33b8524af/artifacts/218oq24l_TCM-Logo.PNG";

const navLinks = [
  { name: "Initiatives", href: "/initiatives", highlight: true },
  { name: "Home", href: "/" },
  {
    name: "Services",
    href: "#",
    dropdown: [
      { name: "Environmental Compliance", href: "/environmental-compliance" },
      { name: "Carbon & CCTS", href: "/carbon-ccts" },
      { name: "Climate Projects", href: "/climate-projects" },
      { name: "Waste Infrastructure", href: "/waste-infrastructure" },
      { name: "Advisory", href: "/advisory" },
    ],
  },
  { name: "Products", href: "/products" },
  { name: "Emission Calculator", href: "/emission-calculator" },
  { name: "Marketplace", href: "/marketplace" },
  { name: "Insights", href: "/insights" },
  { name: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="The Carbon Meridian" className="h-10 lg:h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    className={`px-3 py-2 text-sm font-medium transition-all rounded-lg flex items-center gap-1 ${
                      link.dropdown.some(d => isActive(d.href))
                        ? "text-forest-950 bg-forest-50"
                        : "text-slate-600 hover:text-forest-950 hover:bg-slate-50"
                    }`}
                    data-testid="nav-services"
                  >
                    {link.name}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50" data-testid="services-dropdown">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`block px-4 py-2.5 text-sm hover:bg-emerald-50 transition-all ${
                            isActive(item.href) ? "text-emerald-700 font-semibold bg-emerald-50" : "text-slate-700"
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium transition-all rounded-lg ${
                    link.highlight
                      ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-md flex items-center gap-1.5"
                      : isActive(link.href)
                      ? "text-forest-950 bg-forest-50"
                      : "text-slate-600 hover:text-forest-950 hover:bg-slate-50"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.highlight && <Leaf className="h-4 w-4" />}
                  {link.name}
                </Link>
              )
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name}>
                  <p className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">{link.name}</p>
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`block px-3 py-3 text-sm font-medium rounded-lg pl-6 ${
                        isActive(item.href) ? "text-emerald-700 bg-emerald-50" : "text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block px-3 py-3 text-sm font-medium rounded-lg ${
                    link.highlight
                      ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-center"
                      : isActive(link.href)
                      ? "text-forest-950 bg-forest-50"
                      : "text-slate-600 hover:text-forest-950 hover:bg-slate-50"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
